import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type AppRole = 'ADMIN' | 'ENGINEER' | 'SOLO' | 'CLIENT' | 'OFFICE';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
  allowedRoles?: AppRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  allowedRoles 
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Company onboarding wizard — block workspace until completed (clients use portal setup instead).
  if (user.onboardingStatus === 'PENDING' && user.role !== 'CLIENT') {
    return <Navigate to="/onboarding" replace />;
  }

  // Check role-based access
  if (requiredRole && user.role !== requiredRole) {
    // Redirect based on user's actual role
    const redirectPath = getRoleBasedPath(user.role);
    return <Navigate to={redirectPath} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on user's actual role
    const redirectPath = getRoleBasedPath(user.role);
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

const getRoleBasedPath = (role: string): string => {
  switch (role) {
    case 'CLIENT':
      return '/client/dashboard';
    case 'SOLO':
    case 'ADMIN':
    case 'OFFICE':
    case 'ENGINEER':
      return '/dashboard';
    default:
      return '/login';
  }
};

// Hook for role-based navigation
export const useRoleBasedNavigation = () => {
  const { user } = useAuth();
  
  const getDefaultPath = () => {
    if (!user) return '/login';
    return getRoleBasedPath(user.role);
  };

  const canAccessPath = (path: string): boolean => {
    if (!user) return false;
    
    // Define path access rules
    // NOTE: ENGINEER temporarily shares /dashboard until the dedicated
    // /engineer/* PWA lands (see roadmap Phase 3).
    const pathRules: Record<string, string[]> = {
      '/dashboard': ['ADMIN', 'OFFICE', 'ENGINEER'],
      '/solo-dashboard': ['SOLO'],
      '/engineer': ['ENGINEER'],
      '/client/dashboard': ['CLIENT'],
      '/jobs': ['ADMIN', 'OFFICE', 'ENGINEER'],
      '/clients': ['ADMIN', 'OFFICE'],
      '/certificates': ['ADMIN', 'OFFICE', 'ENGINEER'],
      '/invoices': ['ADMIN', 'OFFICE'],
      '/reports': ['ADMIN'],
    };

    const allowedRoles = pathRules[path];
    return allowedRoles ? allowedRoles.includes(user.role) : true;
  };

  return { getDefaultPath, canAccessPath };
};
